import io from 'socket.io-client';

const URL = process.env.REACT_APP_SOCKET_API_ENDPOINT;
// const URL = 'https://waba-be-socket.its.com.pk'; // Live Socket
// const URL = 'https://waba-be-socket-went.its.com.pk'; // went Socket

const getToken = () => {
	return localStorage.getItem('user_token');
};

class WebSocket {
	static socket = null;

	// Token the live socket was opened with. The JWT rides in the handshake
	// query, so a socket built before the token changed keeps authenticating
	// as the previous user until it is torn down and rebuilt.
	static socketToken = null;

	// How many times a connection has actually been constructed in this tab.
	// Should stay at 1 for the whole session: it only goes up when destroy()
	// has cleared the singleton (logout, token change) and something asks for a
	// socket again. Readable from the console as `WebSocket.instanceCount`.
	static instanceCount = 0;

	static initializeSocket = () => {
		const token = getToken();
		if (!token) {
			return false;
		}
		if (!WebSocket.socket) {
			WebSocket.instanceCount += 1;
			const instanceId = WebSocket.instanceCount;

			WebSocket.socket = io(URL, {
				transports: ['websocket'],
				query: {
					token
				}
			});
			WebSocket.socketToken = token;

			// TEMPORARY DIAGNOSTIC - remove once the duplicate-connection report
			// is settled. This is the ONLY place a connection is ever created, so
			// the instance number distinguishes the two things that both look
			// like "multiple connections" in DevTools:
			//   - one instance reconnecting  -> #1 over and over (socket.io
			//     opening a fresh WebSocket per attempt; old rows show closed).
			//     That is normal and self-healing.
			//   - genuinely duplicated state -> #2, #3, ... each with its own id,
			//     meaning something is nulling the singleton and re-creating it.
			// eslint-disable-next-line no-console
			console.log(`[socket] created instance #${instanceId} -> ${URL}`);

			WebSocket.socket.on('connect', () => {
				// eslint-disable-next-line no-console
				console.log(`[socket] #${instanceId} connected (id=${WebSocket.socket?.id})`);
			});
			WebSocket.socket.on('disconnect', reason => {
				// eslint-disable-next-line no-console
				console.warn(`[socket] #${instanceId} disconnected: ${reason}`);
			});
			WebSocket.socket.io?.on?.('reconnect_attempt', attempt => {
				// eslint-disable-next-line no-console
				console.log(`[socket] #${instanceId} reconnect attempt ${attempt}`);
			});

			// Diagnostics only. There is deliberately NO 'disconnect' handler
			// nulling WebSocket.socket here: socket.io keeps the same instance
			// alive across a drop and re-arms every listener registered on it
			// when it reconnects. Nulling the static ref made the next
			// getSocket() open a SECOND connection while the first was still
			// reconnecting, so every event was delivered twice (and the orphan
			// was never closed).
			WebSocket.socket.on('connect_error', err => {
				// eslint-disable-next-line no-console
				console.warn('[socket] connect_error:', err?.message || err);
			});
			WebSocket.socket.on('error', err => {
				// eslint-disable-next-line no-console
				console.warn('[socket] error:', err?.message || err);
			});
		}
		return true;
	};

	static getSocket = () => {
		const token = getToken();
		// No session => no socket. Also covers logout: the moment the token is
		// gone the connection is dropped instead of lingering, authenticated as
		// the user who just left.
		if (!token) {
			if (WebSocket.socket) {
				WebSocket.destroy();
			}
			return null;
		}
		// Token changed under us (re-login, impersonation). The live socket is
		// authenticated as the old user, so replace it rather than hand it out.
		if (WebSocket.socket && WebSocket.socketToken !== token) {
			WebSocket.destroy();
		}
		if (!WebSocket.socket) {
			if (!WebSocket.initializeSocket()) {
				return null;
			}
		}
		return WebSocket.socket;
	};

	// Explicit teardown, called on logout. Previously the connection only died
	// because logout did a full `window.location.href` navigation; an in-app
	// logout would have left it open on the old token.
	static destroy = () => {
		if (WebSocket.socket) {
			try {
				// TEMPORARY DIAGNOSTIC - pairs with the creation log. A teardown
				// that is not a logout is the thing that would let a second
				// instance be created; the stack shows who asked for it.
				// eslint-disable-next-line no-console
				console.warn('[socket] destroy() called', new Error('destroy call site').stack);
				WebSocket.socket.removeAllListeners();
				WebSocket.socket.disconnect();
			} catch (e) {
				// Nothing useful to do - the reference is being dropped anyway.
			}
		}
		WebSocket.socket = null;
		WebSocket.socketToken = null;
	};
}

export default WebSocket;
